import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, DollarSign, Loader2, PhoneCall, Radio } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api, toastError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import OnboardingChecklist from "@/components/OnboardingChecklist";

const EVENT_TYPE_BADGE = {
  "call.started": "default",
  "call.answered": "secondary",
  "call.ended": "outline",
  "transcript.partial": "secondary",
  "transcript.final": "secondary",
  "recording.ready": "default",
  "recording.deleted": "destructive",
  metric: "outline",
};

function utilColor(pct) {
  if (pct >= 0.85) return "bg-destructive";
  if (pct >= 0.6) return "bg-amber-500";
  return "bg-emerald-500";
}

function MeterRow({ label, used, limit, testid }) {
  const pct = limit > 0 ? used / limit : 0;
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="flex-1 truncate text-sm">{label}</div>
      <div className="w-40 h-2 rounded-full bg-secondary overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all ${utilColor(pct)}`}
          style={{ width: `${Math.min(100, pct * 100)}%` }}
        />
      </div>
      <div className="w-20 text-right font-mono text-sm tabular-nums">
        {used} / {limit}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [concurrency, setConcurrency] = useState(null);
  const [events, setEvents] = useState([]);
  const [streamState, setStreamState] = useState("connecting");
  const esRef = useRef(null);
  const retryRef = useRef(500);

  const fetchInitial = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/concurrency"),
      ]);
      setStats(s.data);
      setConcurrency(c.data);
    } catch (err) {
      toastError(err, "Failed to load dashboard");
    }
  };

  useEffect(() => {
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/stream/events?access_token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;
      setStreamState("connecting");

      es.addEventListener("hello", () => {
        setStreamState("open");
        retryRef.current = 500;
      });
      es.addEventListener("concurrency", (e) => {
        try { setConcurrency(JSON.parse(e.data)); } catch {}
      });
      es.addEventListener("event", (e) => {
        try {
          const ev = JSON.parse(e.data);
          setEvents((prev) => [ev, ...prev].slice(0, 50));
        } catch {}
      });
      es.addEventListener("heartbeat", () => {});
      es.onerror = () => {
        if (cancelled) return;
        setStreamState("reconnecting");
        es.close();
        const delay = Math.min(15000, retryRef.current);
        retryRef.current = Math.min(15000, retryRef.current * 2);
        setTimeout(open, delay);
      };
    };

    open();
    return () => {
      cancelled = true;
      if (esRef.current) esRef.current.close();
    };
  }, [token]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Live Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Real-time view of in-flight calls, per-trunk/per-campaign meters, and a rolling event feed.
          </p>
        </div>
        <Badge
          variant={streamState === "open" ? "default" : streamState === "reconnecting" ? "destructive" : "secondary"}
          data-testid="dashboard-stream-state"
        >
          <Radio className="h-3 w-3 mr-1" /> {streamState}
        </Badge>
      </div>

      {/* Onboarding checklist (Phase 7 §A5) */}
      <OnboardingChecklist />

      {/* stat cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatBig
          label="Global in-flight"
          value={`${stats?.global_in_flight ?? "—"} / ${stats?.global_limit ?? "—"}`}
          testid="dash-stat-global"
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
        />
        <StatBig
          label="Active campaigns"
          value={stats?.active_campaigns ?? "—"}
          testid="dash-stat-campaigns"
        />
        <StatBig
          label="Calls answered today"
          value={stats?.calls_answered_today ?? "—"}
          testid="dash-stat-answered"
          icon={<PhoneCall className="h-4 w-4 text-muted-foreground" />}
        />
        <StatBig
          label="Estimated spend today"
          value={stats?.estimated_spend_today_usd != null
            ? `$${Number(stats.estimated_spend_today_usd).toFixed(2)}`
            : "—"}
          testid="dash-stat-spend"
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* meters */}
        <div className="space-y-6">
          <Section title="Trunks" testid="dash-trunks">
            {!concurrency ? (
              <div className="text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline" /> Loading…
              </div>
            ) : concurrency.per_trunk.length === 0 ? (
              <div className="text-sm text-muted-foreground">No trunks configured.</div>
            ) : (
              concurrency.per_trunk.map((t) => (
                <MeterRow
                  key={t.trunk_id}
                  label={t.name || t.trunk_id}
                  used={t.in_flight}
                  limit={t.limit}
                  testid={`dash-trunk-${t.trunk_id}`}
                />
              ))
            )}
          </Section>

          <Section title="Running campaigns" testid="dash-campaigns">
            {!concurrency ? null : concurrency.per_campaign.length === 0 ? (
              <div className="text-sm text-muted-foreground">No running campaigns.</div>
            ) : (
              concurrency.per_campaign.map((c) => (
                <MeterRow
                  key={c.campaign_id}
                  label={c.name || c.campaign_id}
                  used={c.in_flight}
                  limit={c.limit}
                  testid={`dash-campaign-${c.campaign_id}`}
                />
              ))
            )}
          </Section>
        </div>

        {/* event feed */}
        <Section title="Recent events" testid="dash-feed">
          {events.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Waiting for events…
            </div>
          ) : (
            <ul className="space-y-1.5 max-h-[640px] overflow-y-auto pr-2">
              {events.map((ev, i) => (
                <li
                  key={ev.id || i}
                  className="flex items-start gap-2 text-sm border border-border rounded px-2 py-1.5"
                  data-testid={`dash-event-${i}`}
                >
                  <Badge variant={EVENT_TYPE_BADGE[ev.type] || "outline"}>{ev.type}</Badge>
                  <Link
                    to={ev.job_id ? `/calls/${ev.job_id}` : "#"}
                    className="flex-1 truncate hover:underline"
                  >
                    <span className="text-muted-foreground text-xs mr-2">
                      {new Date(ev.received_at).toLocaleTimeString()}
                    </span>
                    <span className="font-mono text-xs">
                      {JSON.stringify(ev.payload || {}).slice(0, 120)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

function StatBig({ label, value, testid, icon }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        {icon}
      </div>
      <div className="mt-2 text-3xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card" data-testid={testid}>
      <div className="border-b border-border px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </div>
  );
}
